import {
  Paper,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Table,
  Typography,
  IconButton,
  TextField
} from '@mui/material';
import {
  FC,
  useMemo,
  useRef,
  useCallback
} from 'react';
import TableCellWithToolTip from '../../Pages/UserActivityReportTable/tableCellWithTooltip';
import './style.scss';
import {
  convertToSeconds,
  formatTimeWithMoment
} from '../../common/constant';
import LinkIcon from '@mui/icons-material/Link';
import Loader from '../loader';
import CommentIcon from '@mui/icons-material/Comment';
import CommitIcon from '@mui/icons-material/Commit';
type JiraActivityReportTableProps = {
  isLoading: boolean;
  userActivity: any;
  handleOpenDialog: (field: string, issueKey: string, date: string, keyName?: string) => void;
  handleTextInputChange: (value: number) => void;
  handleTextUpdate: () => void;
  selectedIssueKey: string;
  isInputDisabled: boolean;
  selectedPrioritizationValue: number;
};

const JiraActivityReportTable: FC<JiraActivityReportTableProps> = ({
  isLoading,
  userActivity,
  handleOpenDialog,
  handleTextInputChange,
  handleTextUpdate,
  isInputDisabled,
  selectedIssueKey,
  selectedPrioritizationValue
}) => {
  const cellRef = useRef<HTMLTableCellElement>(null);

  const JIRA_TABLE_TITLES = useMemo(
    () => [
      'Project', 'Type', 'Assignee', 'Epic', 'Task', 'Subtask', 'Comment', 'Work Flow',
      'Prioritization', 'Documentation confluence', 'Time Estimated', 'Time Spent',
      'Total Time Spent', 'Git Commit Changes', 'Feedback'
    ],
    []
  );

  const handleInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    let number = parseInt(e.target.value || '0', 10);
    number = Math.max(number, 3);
    handleTextInputChange(number);
  }, [handleTextInputChange]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleTextUpdate();
    }
  }, [handleTextUpdate]);

  const processedUserData = useMemo(() => {
    return userActivity.map((user: any, userIndex: number) => {
      const assigneeWorklogReports = user.activity?.assigneeTask || {};
      const reporterWorkLogsReports = user.activity?.reporterTask || {};

      const mergedWorklogs: Record<string, any[]> = {};

      const allDates = new Set([
        ...Object.keys(assigneeWorklogReports),
        ...Object.keys(reporterWorkLogsReports),
      ]);

      allDates.forEach((date) => {
        mergedWorklogs[date] = [];

        const assigneeLogs = assigneeWorklogReports[date]?.totalWorkLogsInfo?.map((log: any) => ({
          ...log,
          type: 'Worked',
        })) || [];

        const reporterLogs = reporterWorkLogsReports[date]?.totalWorkLogsInfo?.map((log: any) => ({
          ...log,
          type: 'Created',
        })) || [];

        const allLogs = [...assigneeLogs, ...reporterLogs];

        const count: Record<string, number> = {};
        allLogs.forEach((log: any) => {
          const status = log.workflow || '';
          count[status] = (count[status] || 0) + 1;
        });

        const logsWithCount = allLogs.map((log) => ({
          ...log,
          count,
        }));

        mergedWorklogs[date].push(...logsWithCount);
      });

      return {
        ...user,
        key: user.jiraId || userIndex,
        mergedWorklogs
      };
    });
  }, [userActivity]);

  return (
    <TableContainer component={Paper} className="table-container">
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell align="center" sx={{ fontWeight: 700, background: '#43425d', color: '#ffffff' }} width={100}>Person</TableCell>
            <TableCell sx={{ fontWeight: 700, background: '#43425d', color: '#ffffff' }} align="center">JIRA Worklogs</TableCell>
          </TableRow>
        </TableHead>
        {isLoading ? (
          <TableBody>
            <TableRow>
              <TableCell colSpan={15} align="center" sx={{ height: '50vh', verticalAlign: 'middle' }}>
                <Loader loader={isLoading} />
              </TableCell>
            </TableRow>
          </TableBody>
        ) : (
          <TableBody>
            {processedUserData.length > 0 ? processedUserData.map((user: any) => (
              user.jira === 'ACTIVE' ? (
                <TableRow key={user.key}>
                  <TableCell align="center" height={100} width={100} sx={{ verticalAlign: 'middle' }}>
                    <img width={75} height={75} src={user.userProfileImage || ''} alt={user.userName || '-'} />
                    <Typography variant="body1" padding="10px">{user.jira_user_details?.displayName || '-'}</Typography>
                  </TableCell>
                  <TableCell sx={{ padding: 0 }}>
                    <TableContainer className="inner-table-container">
                      <Table stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell colSpan={2}></TableCell>
                            <TableCellWithToolTip title="Total Workflow ticket count" colSpan={2} />
                            <TableCellWithToolTip title="Project" colSpan={1} />
                            {JIRA_TABLE_TITLES.slice(1, 3).map(title => (
                              <TableCell key={title} colSpan={1} align="center" sx={{ width: '40px' }}>{title}</TableCell>
                            ))}
                            {JIRA_TABLE_TITLES.slice(3, 7).map(title => (
                              <TableCell key={title} colSpan={1} align="center" sx={{ width: '120px' }}>{title}</TableCell>
                            ))}
                            {JIRA_TABLE_TITLES.slice(7).map(title => (
                              <TableCellWithToolTip key={title} title={title} colSpan={1} />
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {Object.keys(user.mergedWorklogs).map(date => (
                            user.mergedWorklogs[date].length > 0 ? (
                              user.mergedWorklogs[date].map((worklog: any, i: any) => {
                                const tableCellColor = worklog.originalEstimate
                                  ? convertToSeconds(worklog.totalTimeSpent) > convertToSeconds(worklog.originalEstimate)
                                    ? 'hsla(0, 78%, 82%, .702)'
                                    : '#ffffff'
                                  : '#ffffff';

                                return (
                                  <TableRow key={`${date}-${i}`}>
                                    {i === 0 && (
                                      <>
                                        <TableCell colSpan={2} rowSpan={user.mergedWorklogs[date].length} align="center" sx={{ verticalAlign: 'middle' }} width="7%">
                                          {date}
                                        </TableCell>
                                        <TableCell colSpan={2} rowSpan={user.mergedWorklogs[date].length} align="center" sx={{ verticalAlign: 'middle' }} width="7%">
                                          {Object.entries(worklog.count).map(([status, count]: any[]) => (
                                            <Typography variant="body2" key={status}><strong>{status}:</strong> {count}</Typography>
                                          ))}
                                        </TableCell>
                                      </>
                                    )}
                                    <TableCellWithToolTip title={worklog.projectDetails?.key || '-'} colSpan={1} color={tableCellColor} />
                                    <TableCellWithToolTip title={worklog.type} colSpan={1} color={tableCellColor} />
                                    <TableCell align="center" sx={{ verticalAlign: 'middle', backgroundColor: tableCellColor }}>
                                      <img width={48} height={48} src={worklog.assignee?.assignee?.avatarUrls['48x48'] || ''} alt={worklog.assignee?.assignee?.displayName || '-'} />
                                      <Typography variant="body2">{worklog.assignee?.assignee?.displayName || '-'}</Typography>
                                    </TableCell>
                                    <TableCellWithToolTip title={worklog.epicTaskDetails?.issueSummary || '-'} description={worklog.epicTaskDetails?.issueKey || ''} link={worklog.epicTaskDetails?.issueLink || ''} colSpan={1} color={tableCellColor} />
                                    <TableCellWithToolTip link={worklog.issueType === 'Task_' ? worklog.issueLink || '' : worklog.parentTaskDetails?.issueLink || ''} description={worklog.issueType === 'Task_' ? worklog.issueKey || '-' : worklog.parentTaskDetails?.issueKey || '-'} title={worklog.issueType === 'Task_' ? worklog.issueSummary || '-' : worklog.parentTaskDetails?.issueSummary || '-'} colSpan={1} color={tableCellColor} />
                                    <TableCellWithToolTip link={worklog.issueType === 'Task_' ? '' : worklog.issueLink} description={worklog.issueType === 'Task_' ? '-' : worklog.issueKey} title={worklog.issueType === 'Task_' ? '-' : worklog.issueSummary || '-'} colSpan={1} color={tableCellColor} />
                                    <TableCell align="center" colSpan={1} sx={{ backgroundColor: tableCellColor }}>
                                      <IconButton onClick={() => handleOpenDialog('comments', worklog.issueKey || '', date)}>
                                        <CommentIcon />
                                      </IconButton>
                                    </TableCell>
                                    <TableCellWithToolTip title={worklog.workflow || '-'} colSpan={1} color={tableCellColor} />
                                    {/* {selectedIssueKey === worklog.issueKey + '/' + date && isInputDisabled ? (
                                      <TableCell align="center" colSpan={1} sx={{ backgroundColor: tableCellColor }}>
                                        <TextField
                                          type="number"
                                          value={selectedPrioritizationValue}
                                          onChange={handleInput}                                      /// priortization points edit option will added in future if required
                                          onBlur={handleTextUpdate}
                                          onKeyDown={handleKeyDown}
                                          autoFocus
                                        />
                                      </TableCell>
                                    ) : ( */}
                                      <TableCell ref={cellRef} align="center" colSpan={1} sx={{ backgroundColor: tableCellColor }}
                                        onClick={() =>
                                          worklog.projectDetails?.key !== 'II' &&
                                          handleOpenDialog('priority', worklog.issueKey + '/' + date || '', worklog.prioritizationPoints, 'customfield_11188')
                                        }>
                                        {worklog.prioritizationPoints || '-'}
                                      </TableCell>
                                    {/* )} */}
                                    <TableCell align="center" colSpan={1} sx={{ backgroundColor: tableCellColor }}>
                                      {worklog.confluenceLink ? (
                                        <a href={worklog.confluenceLink} target="_blank" rel="noreferrer">
                                          <LinkIcon style={{ color: '#188bfd' }} />
                                        </a>
                                      ) : <>-</>}
                                    </TableCell>
                                    <TableCellWithToolTip title={worklog.type === 'Worked' ? worklog.originalEstimate || '-' : '-'} colSpan={1} color={tableCellColor} />
                                    <TableCellWithToolTip title={worklog.type === 'Worked' ? formatTimeWithMoment(worklog.totalTimeSpentToday) : `${formatTimeWithMoment(worklog.totalTimeSpentToday)} (${user.jira_user_details?.displayName})` || '-'} colSpan={1} color={tableCellColor} />
                                    <TableCellWithToolTip title={worklog.type === 'Worked' ? worklog.totalTimeSpent || `-` : '-'} colSpan={1} color={tableCellColor} />
                                    <TableCell align="center" colSpan={1} sx={{ backgroundColor: tableCellColor }}>
                                      {worklog.type === 'Worked' ? (
                                        <IconButton onClick={() => handleOpenDialog('commits', worklog.issueKey || '', date)}>
                                          <CommitIcon />
                                        </IconButton>
                                      ) : <>-</>}
                                    </TableCell>
                                    <TableCellWithToolTip title="-" colSpan={1} color={tableCellColor} />
                                  </TableRow>
                                );
                              })
                            ) : (
                              <TableRow key={date}>
                                <TableCell colSpan={2} align="center" sx={{ verticalAlign: 'middle' }} width="7%">
                                  {date}
                                </TableCell>
                                <TableCell colSpan={17} align="center">No Worklogs available for day {date}</TableCell>
                              </TableRow>
                            )
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow key={user.key}>
                  <TableCell colSpan={13} align="center" sx={{ height: '20vh' }}>No active user Found</TableCell>
                </TableRow>
              )
            )) : (
              <TableRow>
                <TableCell colSpan={15} align="center" sx={{ height: '73.8vh', verticalAlign: 'middle', fontSize: '2rem' }}>
                  NO Data Found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        )}
      </Table>
    </TableContainer>
  );
};

export default JiraActivityReportTable;